/**
 * Lightweight pub/sub service for showing the notification prompt.
 * Decoupled from MainPopups — handles its own lifecycle.
 *
 * Has a pending mechanism: if show() is called before subscribe(),
 * the event is queued and replayed when the first subscriber registers.
 * This prevents race conditions with route-level created() hooks.
 */
const subscribers = new Set()
let pendingCall = null
const closeCallbacks = new Set()

function subscribe(fn) {
  subscribers.add(fn)
  if (pendingCall) {
    fn(pendingCall)
    pendingCall = null
  }
  return () => subscribers.delete(fn)
}

function show(params = {}, { onClose } = {}) {
  if (onClose) closeCallbacks.add(onClose)
  const payload = {
    force: !!params.force,
    startAwaiting: !!params.startAwaiting,
    unsupported: !!params.unsupported,
    forceIOS: !!params.forceIOS
  }
  if (!subscribers.size) {
    pendingCall = payload
    return
  }
  subscribers.forEach((fn) => fn(payload))
}

function notifyClosed() {
  closeCallbacks.forEach((fn) => fn())
  closeCallbacks.clear()
}

/**
 * Programmatically close the currently visible prompt (e.g. right after a
 * successful subscription from the profile checkbox). Safe to call when no
 * prompt is visible — it just clears close callbacks and notifies the manager.
 */
function close() {
  notifyClosed()
  subscribers.forEach((fn) => fn({ close: true }))
}

export default { subscribe, show, close, notifyClosed }
